import { FC } from 'react';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import { Alignment } from '@app/shared/enums/alignment.enum';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { shortDateViewRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { getRequiredValidator } from '@core/validations/hook-forms/validators';
import { GridFieldVariation } from '@app/shared/components/grid-controls/grid-field-options';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { useParams } from 'react-router-dom';
import { useLocale } from '@app/core/hooks/useLocale';
import classNames from 'classnames';
import DealThesisEmptyHistoricDataColumns from '../deal-thesis-utils/DealThesisEmptyHistoricDataColumns';
import { DealThesisHeaderTypes } from '@app/shared/models/contracts/enums/shared-enums';
import useDealThesisData from '../deal-thesis-utils/useDealThesisData';

interface DealThesisSectionHeaderProps {
  headerType: DealThesisHeaderTypes;
}

const DealThesisSectionHeader: FC<DealThesisSectionHeaderProps> = ({ headerType }): JSX.Element => {
  const project = useAppSelector((state) => state.project.projectDraft);
  const { caseId } = useParams();
  const { l } = useLocale();
  const valuationDate = project.valuationDate;
  const {
    caseResultsForecastDates,
    historicDataForecastYearsResults,
    showHistoricDataColumns,
    filteredHistoricEventData,
    pwermInputHistoricForecasts,
  } = useDealThesisData(caseId);

  const shouldShowValuationDate =
    headerType === DealThesisHeaderTypes.HistoricalYearsWithNetDebt ||
    headerType === DealThesisHeaderTypes.HistoricalEvents;

  const renderHistoricDataColumns = () => {
    const columns = [];
    if (showHistoricDataColumns) {
      if (headerType === DealThesisHeaderTypes.HistoricalEvents) {
        columns.push(
          ...filteredHistoricEventData.map((historicData) => (
            <th
              key={historicData.historicEventsYear}
              className="table-primary__cell--header-tertiary table-primary__cell--section-start-separator">
              <CellValue value={historicData.historicEventsYear} {...shortDateViewRules} />
            </th>
          ))
        );
      } else {
        columns.push(
          ...historicDataForecastYearsResults.map((historicalData, index) => (
            <th
              key={historicalData.historicYears}
              className="table-primary__cell--header-tertiary table-primary__cell--section-start-separator">
              <CellValue value={pwermInputHistoricForecasts[index].date} {...shortDateViewRules} />
            </th>
          ))
        );
      }
    }
    return columns;
  };

  return (
    <thead className="table-primary__sticky-section table-primary__sticky-section--deal-thesis-header">
      <tr>
        <th className="table-primary__cell--header-tertiary table-primary__cell--section-start-separator" />
        <DealThesisEmptyHistoricDataColumns
          isHistoricEventsSection={headerType === DealThesisHeaderTypes.HistoricalEvents}
          isSectionHeader
          isHeaderTertiary
          isStartSeparator
        />
        {renderHistoricDataColumns()}
        <th
          className={classNames(
            'table-primary__cell--header-tertiary',
            'table-primary__cell--section-start-separator',
            { 'table-primary__cell--align-inherit': !shouldShowValuationDate }
          )}>
          {shouldShowValuationDate ? (
            <CellValue value={valuationDate} {...shortDateViewRules} />
          ) : (
            <GridTextField
              name="historicalYear"
              placeholder={l('_LTM')}
              isInputMasked={false}
              alignment={Alignment.Right}
              variation={GridFieldVariation.TableHeader}
              isDealThesisHeader
              rules={{
                ...getRequiredValidator(),
              }}
            />
          )}
        </th>
        {Object.values(caseResultsForecastDates).map((forecast, index) => (
          <th
            key={index}
            className="table-primary__cell--header-tertiary table-primary__cell--section-start-separator">
            <CellValue value={forecast.forecastId} {...shortDateViewRules} />
          </th>
        ))}
        <th className="table-primary__cell--header-tertiary table-primary__cell--section-start-separator" />
      </tr>
    </thead>
  );
};

export default DealThesisSectionHeader;
